import { useCallback, useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { RiDeleteBinLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DateFilterDropdown from "../../components/DateFilterDropdown";
import NewTableComponent from "../../components/NewTableComponent";
import TripIdFilterDropdown from "../../components/TripIdFilterDropdown";
import VehicleFilterDropdown from "../../components/VehicleFilterDropdown";
import { apiService } from "../../services/apiService";
import {
  fetchAllTripsDropdown,
  fetchVehiclesDropdown,
} from "../../store/Slices/dropdownSlice";
import { getFuelStations } from "../../store/Slices/FuelSlice";
import {
  normalizeStatusLabel,
  pickFirstText,
  toText,
} from "../../utils/helper";

const filterNameMap = {
  refuelAtFilter: "refuelAtFilter",
  priceFilter: "priceFilter",
  statusFilter: "statusFilter",
  tripFilter: "tripFilter",
  vehicleFilter: "vehicleFilter",
};

const defaultFilterTypes = {
  fuelStationIdFilter: "equals",
  refuelAtFilter: "equal_to",
  priceFilter: "greater_than",
  statusFilter: "contains",
  tripFilter: "equals",
  vehicleFilter: "equals",
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10) || "-";

  return date.toISOString().split("T")[0];
};

const formatNumber = (value, suffix = "") => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";

  return `${amount.toLocaleString("en-US", {
    maximumFractionDigits: 2,
  })}${suffix}`;
};

const renderStatusBadge = (status) => {
  const normalized = toText(status).toLowerCase();
  const isCompleted = normalized === "completed";

  return (
    <span
      className={`fuel_listing_status_badge ${isCompleted ? "completed" : "pending"}`}
    >
      {normalizeStatusLabel(status)}
    </span>
  );
};

const renderTripStatusBadge = (status) => {
  const statusConfig = {
    new: { label: "New", bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" },
    in_transit: {
      label: "In Transit",
      bg: "#FEF3C7",
      color: "#D97706",
      border: "#FDE68A",
    },
    "in-transit": {
      label: "In Transit",
      bg: "#FEF3C7",
      color: "#D97706",
      border: "#FDE68A",
    },
    driver_completed: {
      label: "Driver Completed",
      bg: "#E0E7FF",
      color: "#4338CA",
      border: "#C7D2FE",
    },
    completed: {
      label: "Completed",
      bg: "#DCFCE7",
      color: "#15803D",
      border: "#BBF7D0",
    },
    closed: {
      label: "Closed",
      bg: "#F3F4F6",
      color: "#4B5563",
      border: "#E5E7EB",
    },
  };

  const raw = toText(status);
  if (!raw) return "-";

  const normalized = raw.toLowerCase();
  const config = statusConfig[normalized] || {
    label: normalizeStatusLabel(raw),
    bg: "#F3F4F6",
    color: "#4B5563",
    border: "#E5E7EB",
  };

  return (
    <span
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.border}`,
        padding: "4px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: "600",
        display: "inline-block",
        textTransform: "capitalize",
        whiteSpace: "nowrap",
      }}
    >
      {config.label}
    </span>
  );
};

const renderEllipsisCell = (rowId, label, text) => (
  <OverlayTrigger
    placement="top"
    overlay={<Tooltip id={`fuel-${label}-${rowId}`}>{text || "-"}</Tooltip>}
  >
    <span className="fuel_listing_ellipsis_cell">{text || "-"}</span>
  </OverlayTrigger>
);

const normalizeFuelLogRow = (row = {}) => ({
  id: row.id,
  transactionDateTime: row.created_at,
  tripApiId: row.trip_info?.id || row.trip_id,
  tripId: pickFirstText(row.trip_info?.trip_id, row.trip_id),
  regNo: pickFirstText(
    row.trip_info?.vehicle?.registration_number,
    row.trip_info?.vehicle?.registration_number_slug,
    row.vehicle?.registration_number,
    row.vehicle_id,
  ).toUpperCase(),
  fillingStation: pickFirstText(
    row.fuel_station?.name,
    row.fuel_station?.location,
  ),
  fillingStationLocation: row.fuel_station?.location || "",
  fillingDate: row.refuel_at,
  claimedFueled: row.claimed_fueled,
  fuelPrice: row.fuel_price,
  totalFuelCost: pickFirstText(
    row.total_fuel_cost,
    row.expected_total_fuel_cost,
  ),
  fuelType: row.fuel_type,
  billingMode: pickFirstText(
    row.billing_mode,
    row.payment_method,
    row.fuel_station?.billing_mode,
    row.fuel_station?.payment_method,
  ),
  assignedBy: pickFirstText(row.user?.username, row.assign_by),
  status: row.status || row.finance_status || "issued",
  raw: row,
});

const isDeletableLog = (row) => {
  const tripStatus = String(
    row?.trip_info?.status ||
    row?.raw?.trip_info?.status ||
    row?.tripStatus ||
    ""
  ).toLowerCase().trim();

  return tripStatus === "new" || tripStatus === "in_transit" || tripStatus === "in-transit";
};

export const prepareFuelListingPayload = (filters = {}, searchText = "") => {
  const payload = {
    search_text: searchText?.trim() || "",
  };

  Object.keys(filters || {}).forEach((key) => {
    const filter = filters[key];
    if (
      !filter ||
      filter.value === undefined ||
      filter.value === null ||
      filter.value === ""
    )
      return;

    const payloadKey = filterNameMap[key] || key;
    let value = filter.value;
    let filterType =
      filter.filter_type || defaultFilterTypes[payloadKey] || "contains";

    if (Array.isArray(value)) {
      value = value.filter(
        (item) => item !== undefined && item !== null && item !== "",
      );
      if (value.length === 0) return;
      if (payloadKey !== "vehicleFilter" && payloadKey !== "tripFilter") {
        value = value[0];
      }
    }

    if (payloadKey === "statusFilter") {
      filterType = "contains";
      value = String(value).toLowerCase();
    }

    if (payloadKey === "fuelStationIdFilter") {
      filterType = filterType === "contains" ? "equals" : filterType;
      value = Number(value);
      if (!Number.isFinite(value)) return;
    }

    if (payloadKey === "vehicleFilter" || payloadKey === "tripFilter") {
      if (Array.isArray(value)) {
        filterType = "one_of";
        value = value.map((item) => Number(item)).filter(Number.isFinite);
        if (value.length === 0) return;
      } else {
        filterType = filterType === "contains" ? "equals" : filterType;
        value = Number(value);
        if (!Number.isFinite(value)) return;
      }
    }

    if (payloadKey === "priceFilter") {
      filterType = filterType === "equals" ? "equal_to" : filterType;
      value = Number(value);
      if (!Number.isFinite(value)) return;
    }

    if (payloadKey === "refuelAtFilter" && filterType !== "date_range") {
      filterType = filterType === "equals" ? "equal_to" : filterType;
      value = String(value).slice(0, 10);
    }

    payload[payloadKey] = {
      filter_type: filterType,
      value,
    };
  });

  return payload;
};

const FuelListing = ({
  searchText: searchTextProp,
  setSearchText: setSearchTextProp,
  searchFilters: searchFiltersProp,
  setSearchFilters: setSearchFiltersProp,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const vehiclesDropdownData = useSelector(
    (state) =>
      state.dropdowns?.vehiclesDropdownData ||
      state.dropdown?.vehiclesDropdownData ||
      [],
  );
  const fuelStationDropdownData = useSelector(
    (state) => state.fuel?.getFuelStationsData || [],
  );
  const allTripsDropdownData = useSelector(
    (state) => state.dropdowns?.allTripsDropdownData || [],
  );

  const [listingData, setListingData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [internalSearchText, setInternalSearchText] = useState("");
  const [internalSearchFilters, setInternalSearchFilters] = useState({});
  const [loading, setLoading] = useState(false);

  const searchText = searchTextProp !== undefined ? searchTextProp : internalSearchText;
  const setSearchText = setSearchTextProp || setInternalSearchText;
  const searchFilters = searchFiltersProp !== undefined ? searchFiltersProp : internalSearchFilters;
  const setSearchFilters = setSearchFiltersProp || setInternalSearchFilters;

  useEffect(() => {
    dispatch(fetchVehiclesDropdown());
    dispatch(getFuelStations());
    dispatch(fetchAllTripsDropdown());
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;

    const fetchFuelLogs = async () => {
      setLoading(true);
      try {
        const payload = prepareFuelListingPayload(searchFilters, searchText);
        const response = await apiService.getFuelLogsPaginated(
          { page: currentPage, limit },
          payload,
        );

        if (!isMounted) return;

        const data = response?.data || {};
        const results = Array.isArray(data?.results) ? data.results : [];
        const meta = data?.meta || {};

        setListingData(results.map(normalizeFuelLogRow));
        setTotalItems(
          Number(meta?.totalItems || data?.total || results.length),
        );
        setTotalPages(Number(meta?.totalPages || 1));
      } catch (error) {
        if (!isMounted) return;
        setListingData([]);
        setTotalItems(0);
        setTotalPages(1);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchFuelLogs();

    return () => {
      isMounted = false;
    };
  }, [currentPage, limit, searchFilters, searchText]);

  const vehicleDropdownOptions = useMemo(
    () =>
      (vehiclesDropdownData || []).map((vehicle) => ({
        label:
          vehicle.label ||
          vehicle.registration_number ||
          String(vehicle.value || vehicle.id || ""),
        value: String(vehicle.value ?? vehicle.id ?? ""),
      })),
    [vehiclesDropdownData],
  );

  const tripDropdownOptions = useMemo(
    () =>
      (allTripsDropdownData || []).map((trip) => ({
        label: trip.label || trip.trip_id || String(trip.value || trip.id || ""),
        value: String(trip.value ?? trip.id ?? ""),
      })),
    [allTripsDropdownData],
  );

  const fuelStationDropdownOptions = useMemo(
    () =>
      (fuelStationDropdownData || []).map((fuelStation) => ({
        label:
          fuelStation.label ||
          fuelStation.name ||
          String(fuelStation.value || fuelStation.id || ""),
        value: String(fuelStation.value ?? fuelStation.id ?? ""),
      })),
    [fuelStationDropdownData],
  );

  const columns = useMemo(
    () => [
      {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
        name: "Filling Station",
        selector: (row) => row.fillingStation || "-",
        cell: (row) =>
          renderEllipsisCell(row.id, "station", row.fillingStation),
        tableColumn: true,
        filterName: "fuelStationIdFilter",
        type: "select",
        options: fuelStationDropdownOptions,
        minWidth: "230px",
      },
      {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
        name: "Filling Date",
        selector: (row) => row.fillingDate,
        cell: (row) => formatDateOnly(row.fillingDate),
        tableColumn: true,
        filterName: "refuelAtFilter",
        type: "date_range",
        filterComponent: DateFilterDropdown,
        minWidth: "140px",
      },
      {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
        name: "Reg. No.",
        selector: (row) => row.regNo || "-",
        tableColumn: true,
        filterName: "vehicleFilter",
        type: "select",
        isMulti: true,
        filterComponent: VehicleFilterDropdown,
        options: vehicleDropdownOptions,
        minWidth: "120px",
      },
      {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
        name: "Trip ID",
        selector: (row) => row.tripId || "-",
        cell: (row) => (
          <span
            className="hyperlink"
            role="button"
            tabIndex={0}
            onClick={() => row.tripApiId && navigate(`/trips/${row.tripApiId}`)}
          >
            {row.tripId || "-"}
          </span>
        ),
        tableColumn: true,
        filterName: "tripFilter",
        type: "select",
        isMulti: true,
        filterComponent: TripIdFilterDropdown,
        options: tripDropdownOptions,
        minWidth: "180px",
      },
      {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
        name: "Fuel Type",
        selector: (row) => normalizeStatusLabel(row.fuelType),
        tableColumn: false,
        minWidth: "120px",
      },

      {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
        name: "Qty (Ltrs)",
        selector: (row) => formatNumber(row.claimedFueled),
        tableColumn: false,
        minWidth: "120px",
      },
      {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
        name: "Fuel Price",
        selector: (row) => formatNumber(row.fuelPrice),
        tableColumn: true,
        filterName: "priceFilter",
        type: "number",
        minWidth: "120px",
      },
      {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
        name: "Fuel Amount",
        selector: (row) => formatNumber(row.totalFuelCost),
        tableColumn: false,
        minWidth: "130px",
      },

      {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
        name: "Status",
        selector: (row) => row.status,
        cell: (row) => renderStatusBadge(row.status),
        tableColumn: false,
        filterName: "statusFilter",
        type: "text",
        options: [],
        minWidth: "130px",
      },
      {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
        name: "Issued By",
        selector: (row) => row.assignedBy || "-",
        cell: (row) => renderEllipsisCell(row.id, "issued-by", row.assignedBy),
        tableColumn: false,
        minWidth: "160px",
      },
    ],
    [
      fuelStationDropdownOptions,
      navigate,
      tripDropdownOptions,
      vehicleDropdownOptions,
    ],
  );

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleLimitChange = (value) => {
    setLimit(value);
    setCurrentPage(1);
  };

  const handleSearchFiltersChange = (updater) => {
    setCurrentPage(1);
    setSearchFilters((previousFilters) =>
      typeof updater === "function" ? updater(previousFilters) : updater,
    );
  };

  return (
    <div className="trip_listing_container fuel_listing_container">
      <NewTableComponent
        columns={columns}
        data={listingData}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={handleLimitChange}
        totalItems={totalItems}
        totalPages={totalPages}
        onSearch={handleSearch}
        searchValue={searchText}
        loading={loading}
        paginationLoading={loading}
        setSearchFilters={handleSearchFiltersChange}
        searchFilters={searchFilters}
      />
    </div>
  );
};

export default FuelListing;
